import { TYPES } from '@/engine/ioc/types';
import { inject, injectable } from 'inversify';
import type {
  IMeasurementViewModel,
  IObjectViewModel,
  IViewFactory,
  IViewFactoryProvider,
  JSONConfig
} from './interfaces';

import type { ViewEntity } from './models/viewEntity/view.entity.ts';
import { action, computed, makeObservable, observable } from 'mobx';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import { CORE_TYPES } from '@/engine/app/modules/core';
import type { ObservabilityLoggerService } from '@/app.obresvability/loggerService/observabilityLogger.service.ts';
import { DisposablesRegistry, IRegistry } from '../Utilities/DisposablesRegistry.ts';
import { IDisposable } from '../Utilities/IDisposable.ts';

@injectable()
export class ViewsService {
  private readonly _viewFactoryProvider: IViewFactoryProvider<JSONConfig.Config>;

  @observable private _currentView: ViewEntity | null = null;

  @observable private _views: Array<ViewEntity> = [];

  private readonly _planningAlgorithm: PlanningAlgorithm;

  private readonly _loggerService: ObservabilityLoggerService;

  private readonly _currentViewLifetime = new DisposablesRegistry<string>();

  public constructor(
    @inject(TYPES.IViewFactoryProvider)
    viewFactoryProvider: IViewFactoryProvider<JSONConfig.Config>,
    @inject(TYPES.PlanningAlgorithm) planningAlgorithm: PlanningAlgorithm,
    @inject(CORE_TYPES.ObservabilityLoggerService) loggerService: ObservabilityLoggerService
  ) {
    makeObservable(this);
    this._viewFactoryProvider = viewFactoryProvider;
    this._planningAlgorithm = planningAlgorithm;
    this._loggerService = loggerService;
  }

  public loadViewsConfig(config: JSONConfig.Config): Promise<void> {
    const viewFactory: IViewFactory = this._viewFactoryProvider.makeFactory(config);
    this._views = config.views.map((view: JSONConfig.View) => viewFactory.createViewFromJSON(view));
    return Promise.resolve();
  }

  public checkViewsForAvailabilityAndFilter(): void {
    this._views = this._views.filter((view): boolean => {
      if (view.availabilityDescriptor === undefined) return true;
      else return this._planningAlgorithm.getValue(view.availabilityDescriptor, DT.BooleanT)?.value === true;
    });
  }

  @action
  public addObjectToCurrentView(object: IObjectViewModel): void {
    this.currentView?.addObject(object);
  }

  @action
  public removeObjectFromCurrentView(object: IObjectViewModel): void {
    this.currentView?.removeObject(object);
  }

  @action
  public addMeasurementToCurrentView(object: IMeasurementViewModel): void {
    this.currentView?.addMeasurement(object);
  }

  @action
  public removeMeasurementFromCurrentView(object: IMeasurementViewModel): void {
    this.currentView?.removeMeasurement(object);
  }

  @action
  public removeFromViewByDescriptor(descriptor: string): void {
    this.currentView?.removeByDescriptor(descriptor);
  }

  @action
  public removeFromViewsByDescriptor(descriptor: string): void {
    this._views.forEach((view) => view.removeByDescriptor(descriptor));
  }

  @action
  public setCurrentView(view: ViewEntity): void {
    this._loggerService.startView(view.getDescriptor());
    this._currentViewLifetime.dispose();
    this._currentView = view;
  }

  @action
  public addView(view: ViewEntity): void {
    this._views.push(view);
  }

  @action
  public removeView(view: ViewEntity): void {
    this._views = this._views.filter((v) => v !== view);
  }

  @computed
  public get views(): Array<ViewEntity> {
    return this._views;
  }

  @computed
  public get currentViewOrThrow(): ViewEntity {
    if (this._currentView === null) {
      throw new Error('Current view is not set.');
    }
    return this._currentView;
  }

  @computed
  public get currentView(): ViewEntity | null {
    return this._currentView;
  }

  public getViewByDescriptor(descriptor: string): ViewEntity | undefined {
    return this._views.find((view) => descriptor === view.getDescriptor());
  }

  public getCurrentViewLifetime(): IRegistry<string, IDisposable> {
    return this._currentViewLifetime;
  }
}
